import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  alpha,
  useTheme,
  Paper,
  Tooltip,
  IconButton as MuiIconButton,
  Stack,
} from "@mui/material";
import {
  ExpandLess,
  ExpandMore,
  CameraAlt as CameraIcon,
  Thermostat as ThermostatIcon,
  Opacity as OpacityIcon,
  LocationOn as LocationIcon,
  FiberManualRecord as FiberManualRecordIcon,
  KeyboardArrowDown,
  KeyboardArrowUp,
} from "@mui/icons-material";
import { backend_url } from "../App";

function SensorList({ sensorSelect }) {
  const theme = useTheme();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openItems, setOpenItems] = useState({});

  const config = React.useMemo(
    () => ({
      headers: {
        Authorization: `Token ${localStorage.getItem("token")}`,
      },
    }),
    [],
  );

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(`${backend_url}/core/api/towers/`, config);
        if (!response.ok) {
          throw new Error(`HTTP error: ${response.status}`);
        }
        const actualData = await response.json();
        setData(actualData.results);
        setError(null);
      } catch (err) {
        setError(err.message);
        setData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [config]);

  const handleToggle = (index) => {
    setOpenItems((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  // Helper function to get camera status
  const getCameraStatus = (camera) => {
    const isLive = camera.live === true;
    const lastReportedAt = camera.last_reported_at;

    if (!isLive) {
      return {
        label: "UNARMED",
        color: "error",
        iconColor: "#d32f2f",
        chipColor: "error",
        pulse: false,
      };
    }

    if (!lastReportedAt) {
      return {
        label: "OFFLINE",
        color: "warning",
        iconColor: "#ed6c02",
        chipColor: "warning",
        pulse: false,
      };
    }

    const lastReported = new Date(lastReportedAt);
    const now = new Date();
    const diffInHours = (now - lastReported) / (1000 * 60 * 60);

    if (diffInHours <= 1) {
      return {
        label: "ARMED",
        color: "success",
        iconColor: "#2e7d32",
        chipColor: "success",
        pulse: true,
      };
    } else {
      return {
        label: "OFFLINE",
        color: "warning",
        iconColor: "#ed6c02",
        chipColor: "warning",
        pulse: false,
      };
    }
  };

  // Helper function to get overall tower status based on its cameras
  const getTowerStatus = (tower) => {
    if (!tower.cameras || tower.cameras.length === 0) {
      return {
        label: "NO CAMERAS",
        iconColor: "#999",
        pulse: false,
      };
    }

    // Check if any camera is ARMED
    const hasArmed = tower.cameras.some((cam) => {
      const status = getCameraStatus(cam);
      return status.label === "ARMED";
    });

    if (hasArmed) {
      return {
        label: "ARMED",
        iconColor: "#2e7d32",
        pulse: true,
      };
    }

    // Check if any camera is ONLINE (but not armed)
    const hasOnline = tower.cameras.some((cam) => cam.live === true);
    if (hasOnline) {
      return {
        label: "ONLINE",
        iconColor: "#ed6c02",
        pulse: false,
      };
    }

    return {
      label: "UNARMED",
      iconColor: "#d32f2f",
      pulse: false,
    };
  };

  // Helper function to get relative time
  const getRelativeTime = (dateString) => {
    if (!dateString) return "Never";

    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    const diffInHours = Math.floor(diffInMinutes / 60);
    const diffInDays = Math.floor(diffInHours / 24);
    const diffInMonths = Math.floor(diffInDays / 30);
    const diffInYears = Math.floor(diffInDays / 365);

    if (diffInSeconds < 60) {
      return `${diffInSeconds}s ago`;
    } else if (diffInMinutes < 60) {
      return `${diffInMinutes}m ago`;
    } else if (diffInHours < 24) {
      return `${diffInHours}h ago`;
    } else if (diffInDays < 30) {
      return `${diffInDays}d ago`;
    } else if (diffInMonths < 12) {
      return `${diffInMonths}mo ago`;
    } else {
      return `${diffInYears}y ago`;
    }
  };

  const handleCameraClick = useCallback(
    (camera) => {
      sensorSelect({
        ...camera,
        isCamera: true,
        user: true,
        location: {
          lat: camera.latitude,
          lng: camera.longitude,
        },
        id: camera.id,
        description: camera.description,
        latitude: camera.latitude,
        longitude: camera.longitude,
        live: camera.live,
        start_angle: camera.start_angle,
        end_angle: camera.end_angle,
        video_interval: camera.video_interval,
        name: camera.description,
        lat: camera.latitude,
        lng: camera.longitude,
        last_reported_at: camera.last_reported_at,
      });
    },
    [sensorSelect],
  );

  const handleTowerClick = useCallback(
    (tower) => {
      sensorSelect({ ...tower, isTower: true });
    },
    [sensorSelect],
  );

  const handleSensorClick = useCallback(
    (sensor) => {
      sensorSelect({ ...sensor, isSensor: true });
    },
    [sensorSelect],
  );

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
          minHeight: 400,
        }}
      >
        <CircularProgress size={40} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          Error loading devices: {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        height: "100%",
        bgcolor: "#d5d2d2",
        borderRadius: 5,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 1,
          background: "linear-gradient(135deg, #454545 0%, #2d2d2d 100%)",
          color: "white",
        }}
      >
        <Typography
          variant="h6"
          sx={{ fontWeight: 600, ml: 1, fontSize: "1rem" }}
        >
          Cameras & Sensors
        </Typography>
        <Typography
          variant="caption"
          sx={{ opacity: 0.8, ml: 1, fontSize: "0.7rem" }}
        >
          Select a device to view details
        </Typography>
      </Box>

      <Divider sx={{ my: 0 }} />

      {/* List with custom scrollbar */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          p: 0,
          "&::-webkit-scrollbar": {
            width: "6px",
          },
          "&::-webkit-scrollbar-track": {
            background: "rgba(0,0,0,0.05)",
            borderRadius: "10px",
            margin: "4px 0",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "linear-gradient(180deg, #454545, #2d2d2d)",
            borderRadius: "10px",
            "&:hover": {
              background: "linear-gradient(180deg, #555555, #3d3d3d)",
            },
          },
          scrollbarWidth: "thin",
          scrollbarColor: "#454545 rgba(0,0,0,0.05)",
        }}
      >
        {data && data.length > 0 ? (
          data.map((item, index) => {
            const towerStatus = getTowerStatus(item);

            return (
              <Box key={index} sx={{ mb: 0 }}>
                <ListItemButton
                  onClick={(e) => {
                    e.stopPropagation();
                    handleToggle(index);
                  }}
                  sx={{
                    borderRadius: 1,
                    py: 0.3,
                    bgcolor: openItems[index]
                      ? alpha(theme.palette.primary.main, 0.08)
                      : "transparent",
                    "&:hover": {
                      bgcolor: alpha(theme.palette.primary.main, 0.04),
                    },
                    "&:focus": {
                      outline: "none !important",
                    },
                    "&:focus-visible": {
                      outline: "none !important",
                    },
                    "&.Mui-focusVisible": {
                      outline: "none !important",
                      boxShadow: "none !important",
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <LocationIcon
                      sx={{ color: theme.palette.primary.main, fontSize: 20 }}
                    />
                  </ListItemIcon>

                  <ListItemText
                    primary={
                      <Box
                        component="span"
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                        }}
                      >
                        <FiberManualRecordIcon
                          sx={{
                            fontSize: 10,
                            color: towerStatus.iconColor,
                            animation: towerStatus.pulse
                              ? "pulse 1.5s infinite"
                              : "none",
                            "@keyframes pulse": {
                              "0%": { opacity: 1 },
                              "50%": { opacity: 0.3 },
                              "100%": { opacity: 1 },
                            },
                          }}
                        />
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            fontSize: "0.95rem",
                          }}
                        >
                          {item.name}
                        </Typography>
                      </Box>
                    }
                    secondary={`${item.cameras?.length || 0} cameras, ${item.sensors?.length || 0} sensors`}
                    secondaryTypographyProps={{
                      fontSize: "0.8rem",
                    }}
                  />

                  <MuiIconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggle(index);
                    }}
                    sx={{
                      p: 0.5,
                      borderRadius: "50%",
                      transition: "transform 0.3s ease",
                      transform: openItems[index]
                        ? "rotate(180deg)"
                        : "rotate(0deg)",
                      "&:focus": {
                        outline: "none !important",
                      },
                      "&:focus-visible": {
                        outline: "none !important",
                      },
                      "&.Mui-focusVisible": {
                        outline: "none !important",
                        boxShadow: "none !important",
                      },
                    }}
                  >
                    <KeyboardArrowDown sx={{ fontSize: 22, color: "#666" }} />
                  </MuiIconButton>
                </ListItemButton>

                <Collapse in={openItems[index]} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding sx={{ pl: 1, py: 0 }}>
                    {/* Cameras Section */}
                    {item.cameras && item.cameras.length > 0 && (
                      <>
                        <Typography
                          variant="caption"
                          sx={{
                            px: 2,
                            pt: 0.5,
                            pb: 0,
                            display: "block",
                            color: "text.secondary",
                            fontWeight: 600,
                            fontSize: "0.65rem",
                          }}
                        >
                          📷 CAMERAS
                        </Typography>
                        {item.cameras.map(
                          (camera, camIndex) =>
                            camera.live && (
                              <ListItemButton
                                key={camIndex}
                                onClick={() => handleCameraClick(camera)}
                                sx={{
                                  borderRadius: 1,
                                  py: 0.2,
                                  pl: 3,
                                  "&:hover": {
                                    bgcolor: alpha(
                                      theme.palette.primary.main,
                                      0.04,
                                    ),
                                  },
                                  "&:focus": {
                                    outline: "none !important",
                                  },
                                  "&:focus-visible": {
                                    outline: "none !important",
                                  },
                                  "&.Mui-focusVisible": {
                                    outline: "none !important",
                                    boxShadow: "none !important",
                                  },
                                }}
                              >
                                <ListItemIcon sx={{ minWidth: 28 }}>
                                  <CameraIcon
                                    sx={{
                                      fontSize: 16,
                                      color: theme.palette.info.main,
                                    }}
                                  />
                                </ListItemIcon>
                                <ListItemText
                                  primary={
                                    <Box
                                      component="span"
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 0.5,
                                      }}
                                    >
                                      <Box
                                        component="span"
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 0.3,
                                        }}
                                      >
                                        <Typography
                                          variant="body2"
                                          sx={{
                                            fontSize: "0.78rem",
                                            fontWeight: 500,
                                          }}
                                        >
                                          {camera.description}
                                        </Typography>
                                      </Box>
                                      <Chip
                                        label={getCameraStatus(camera).label}
                                        size="small"
                                        color={
                                          getCameraStatus(camera).chipColor
                                        }
                                        sx={{
                                          height: 16,
                                          fontSize: "0.55rem",
                                          fontWeight: 700,
                                          "& .MuiChip-label": {
                                            px: 0.6,
                                            py: 0,
                                          },
                                        }}
                                      />
                                    </Box>
                                  }
                                  secondary={
                                    <Box
                                      component="span"
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 0.5,
                                        mt: 0.2,
                                      }}
                                    >
                                      <Tooltip
                                        title={`Last ping: ${camera.last_reported_at ? new Date(camera.last_reported_at).toLocaleString() : "Never"}`}
                                        arrow
                                      >
                                        <Typography
                                          variant="caption"
                                          sx={{
                                            color:
                                              getCameraStatus(camera).iconColor,
                                            fontWeight: 500,
                                            fontSize: "0.6rem",
                                          }}
                                        >
                                          {getRelativeTime(
                                            camera.last_reported_at,
                                          )}
                                        </Typography>
                                      </Tooltip>
                                    </Box>
                                  }
                                  secondaryTypographyProps={{
                                    component: "span",
                                  }}
                                />
                              </ListItemButton>
                            ),
                        )}
                      </>
                    )}

                    {/* Sensors Section */}
                    {item.sensors && item.sensors.length > 0 && (
                      <>
                        <Typography
                          variant="caption"
                          sx={{
                            px: 2,
                            py: 0.5,
                            display: "block",
                            color: "text.secondary",
                            fontWeight: 600,
                            fontSize: "0.65rem",
                          }}
                        >
                          📊 SENSORS
                        </Typography>
                        {item.sensors.map((sensor, sensorIndex) => (
                          <ListItemButton
                            key={sensorIndex}
                            onClick={() => handleSensorClick(sensor)}
                            sx={{
                              borderRadius: 1,
                              py: 0.2,
                              pl: 3,
                              "&:hover": {
                                bgcolor: alpha(
                                  theme.palette.primary.main,
                                  0.04,
                                ),
                              },
                              "&:focus": {
                                outline: "none !important",
                              },
                              "&:focus-visible": {
                                outline: "none !important",
                              },
                              "&.Mui-focusVisible": {
                                outline: "none !important",
                                boxShadow: "none !important",
                              },
                            }}
                          >
                            <ListItemIcon sx={{ minWidth: 28 }}>
                              {sensor.sensor_type === "Temperature" ? (
                                <ThermostatIcon
                                  sx={{
                                    fontSize: 16,
                                    color: theme.palette.warning.main,
                                  }}
                                />
                              ) : (
                                <OpacityIcon
                                  sx={{
                                    fontSize: 16,
                                    color: theme.palette.info.main,
                                  }}
                                />
                              )}
                            </ListItemIcon>
                            <ListItemText
                              primary={sensor.name}
                              primaryTypographyProps={{
                                fontSize: "0.78rem",
                              }}
                              secondary={sensor.sensor_type}
                              secondaryTypographyProps={{
                                fontSize: "0.6rem",
                              }}
                            />
                          </ListItemButton>
                        ))}
                      </>
                    )}
                  </List>
                </Collapse>
              </Box>
            );
          })
        ) : (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <Typography variant="body2" color="text.secondary">
              No devices found
            </Typography>
          </Box>
        )}
      </Box>
    </Paper>
  );
}

export default SensorList;
